import { JSX, ReactNode } from "react";
import { Col, Divider, Row } from "antd";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import color from "../../../resource/color";
import image from "../../../resource/image";
import { ShopTierCompanyConfig } from "../../ShopTier/config";
import { ConfigTierBenefit } from "../../ShopTier/mockData";
import TierReviewList from "../TierReviewList";
import { Step2Result } from "./Step2";

interface Props {
  mode: "create" | "edit";
  config: ShopTierCompanyConfig;
  year: string;
  data: Step2Result | null;
  sourceTiers?: ConfigTierBenefit[];
  startFrom?: string;
  onBack: () => void;
  onConfirm: () => void;
}

function SectionReview({
  title,
  count,
  unit,
  children,
}: {
  title: string;
  count: number;
  unit: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <Text fontWeight={700} fontSize={16} color="Text2">
          {title}
        </Text>
        <Text fontSize={13} color="Text3">
          {count} {unit}
        </Text>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function ReviewBox({ children }: { children: ReactNode }) {
  return (
    <div style={{ background: color.background1, borderRadius: 8, padding: "12px 14px" }}>
      {children}
    </div>
  );
}

function Step3({ mode, config, year, data, sourceTiers = [], startFrom, onBack, onConfirm }: Props): JSX.Element {
  if (!data) {
    return (
      <div style={{ marginTop: 24 }}>
        <Text fontSize={14} color="Text3">ยังไม่มีข้อมูลให้ตรวจสอบ กรุณากลับไปกรอกข้อมูลในขั้นตอนก่อนหน้า</Text>
        <Divider />
        <Row justify="end">
          <Col>
            <Button typeButton="border-primary" title="ย้อนกลับ" style={{ width: "auto" }} onClick={onBack} />
          </Col>
        </Row>
      </div>
    );
  }

  const totalBenefits = data.tiers.reduce((sum, t) => sum + t.benefits.length, 0);
  const bannerLabel = data.bannerUrl ? "อัปโหลดเอง" : "ภาพจากทางบริษัท";
  const showDiff = mode === "edit" || startFrom === "copy";

  return (
    <div style={{ marginTop: 24 }}>
      <Row justify="space-between" wrap={false} gutter={16}>
        <Col>
          <Text level={3} fontWeight={700} color="Text2">
            ชุดสิทธิประโยชน์ปี {year}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Text fontSize={14} color="Text3">
              {config.tiers.length} ระดับ · {totalBenefits} สิทธิประโยชน์ · แบนเนอร์: {bannerLabel}
            </Text>
          </div>
        </Col>
        <Col flex="none">
          <img
            src={data.bannerUrl || image.tierBanner}
            alt="แบนเนอร์"
            style={{ width: 230, height: 170, objectFit: "cover", borderRadius: 12, border: `1px solid ${color.border}` }}
          />
        </Col>
      </Row>
      <Divider />

      <TierReviewList
        config={config}
        tiers={data.tiers}
        cardImageOverrides={data.cardImageOverrides}
        colorOverrides={data.colorOverrides}
        originalTiers={showDiff ? sourceTiers : []}
        changedTierKeys={showDiff ? data.changedTierKeys : []}
      />

      <SectionReview title="วิธีการทำงาน" count={data.cards.length} unit="การ์ด">
        {data.cards.map((c, idx) => (
          <ReviewBox key={idx}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {c.iconUrl && (
                <img src={c.iconUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
              )}
              <div>
                <Text fontWeight={700} fontSize={14} color="Text2">{c.title || "-"}</Text>
                {c.description && (
                  <div style={{ marginTop: 2 }}>
                    <Text fontSize={13} color="Text3">{c.description}</Text>
                  </div>
                )}
              </div>
            </div>
          </ReviewBox>
        ))}
      </SectionReview>

      <SectionReview title="คำถามที่พบบ่อย" count={data.faqs.length} unit="คำถาม">
        {data.faqs.map((f, idx) => (
          <ReviewBox key={idx}>
            <Text fontWeight={700} fontSize={14} color="Text2">{f.question || "-"}</Text>
            {f.answer && (
              <div style={{ marginTop: 2 }}>
                <Text fontSize={13} color="Text3">{f.answer}</Text>
              </div>
            )}
          </ReviewBox>
        ))}
      </SectionReview>

      <SectionReview title="กติกาและเงื่อนไข" count={data.rules.length} unit="หัวข้อ">
        {data.rules.map((r, idx) => (
          <ReviewBox key={idx}>
            <Text fontWeight={700} fontSize={14} color="Text2">{r.title || "-"}</Text>
            <div
              style={{ marginTop: 4, fontFamily: "Sarabun", fontSize: 13, color: color.Text3, lineHeight: "20px" }}
              dangerouslySetInnerHTML={{ __html: r.content }}
            />
          </ReviewBox>
        ))}
      </SectionReview>

      <Divider />
      <Row justify="start" gutter={8}>
        <Col>
          <Button typeButton="border-primary" title="ย้อนกลับ" style={{ width: "auto" }} onClick={onBack} />
        </Col>
        <Col>
          <Button
            typeButton="primary"
            title={mode === "edit" ? "ยืนยันการแก้ไข" : "ยืนยันการสร้าง"}
            style={{ width: "auto" }}
            onClick={onConfirm}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Step3;
